/*
 * Paul Beuscher — Smart fuzzy search (client-side, no app, no build step).
 *
 * Gives the storefront search "intelligence" without any third-party app:
 *   - word-order independent      ("home guitar" == "guitar home")
 *   - typo tolerance              ("hom guitar" -> "home guitar")
 *   - de-concatenation            ("guitarhome" -> "guitar home")
 *   - prefix matching             ("gui" -> "guitare")
 *   - accent-insensitive          ("ukulele" == "ukulélé")
 *
 * Rich dropdown (matches the native predictive design):
 *   left  -> Popular suggestions / Categories / Pages
 *   right -> Products grid with image, title, description, SKU,
 *            price and struck-through compare-at price
 *   footer-> "View all N items"
 *
 * The catalog is loaded once from /products.json (cached in localStorage) and
 * matched in the browser. Config comes from window.PBSmartSearchConfig, set by
 * snippets/pb-smart-search.liquid. If config or /products.json is missing, the
 * script no-ops and native search keeps working.
 */
(function () {
  'use strict';

  var CFG = window.PBSmartSearchConfig;
  if (!CFG || !CFG.enabled) return;

  // ----------------------------------------------------------------------- //
  // Text normalization                                                      //
  // ----------------------------------------------------------------------- //

  // Lowercase, strip diacritics (é -> e, important for French), collapse
  // anything non-alphanumeric to spaces.
  function normalize(str) {
    if (!str) return '';
    return String(str)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\s]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokenize(str) {
    var n = normalize(str);
    return n ? n.split(' ') : [];
  }

  function stripHtml(html) {
    if (!html) return '';
    return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  // Levenshtein with an early-exit ceiling (fast enough to scan the vocabulary
  // on each keystroke).
  function editDistance(a, b, max) {
    var al = a.length, bl = b.length;
    if (Math.abs(al - bl) > max) return max + 1;
    if (al === 0) return bl;
    if (bl === 0) return al;
    var prev = new Array(bl + 1), curr = new Array(bl + 1), i, j;
    for (j = 0; j <= bl; j++) prev[j] = j;
    for (i = 1; i <= al; i++) {
      curr[0] = i;
      var rowMin = curr[0], ca = a.charCodeAt(i - 1);
      for (j = 1; j <= bl; j++) {
        var cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
        var del = prev[j] + 1, ins = curr[j - 1] + 1, sub = prev[j - 1] + cost;
        var v = del < ins ? del : ins;
        if (sub < v) v = sub;
        curr[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      var tmp = prev; prev = curr; curr = tmp;
    }
    return prev[bl];
  }

  function maxEditsFor(len) {
    if (len <= 2) return 0;
    if (len <= 4) return 1;
    if (len <= 7) return 2;
    return 3;
  }

  // ----------------------------------------------------------------------- //
  // The index                                                               //
  // ----------------------------------------------------------------------- //

  var Index = {
    ready: false, loading: null, failed: false,
    records: [], vocab: new Map(), vocabList: [], byHandle: {}
  };

  var FIELD_WEIGHTS = { title: 1.0, vendor: 0.92, type: 0.88, tags: 0.72 };

  function buildRecord(p) {
    var price = null, compareAt = null, sku = '', available = false;
    if (p.variants && p.variants.length) {
      for (var i = 0; i < p.variants.length; i++) {
        var v = p.variants[i];
        var pr = parseFloat(v.price);
        if (!isNaN(pr) && (price === null || pr < price)) {
          price = pr;
          var ca = parseFloat(v.compare_at_price);
          compareAt = !isNaN(ca) && ca > pr ? ca : null;
          sku = v.sku || '';
        }
        if (v.available) available = true;
      }
    }
    var image = null;
    if (p.images && p.images.length && p.images[0]) image = p.images[0].src || null;
    else if (p.featured_image) image = p.featured_image;

    return {
      id: p.id,
      handle: p.handle,
      url: CFG.productUrlBase + p.handle,
      title: p.title || '',
      vendor: p.vendor || '',
      type: p.product_type || '',
      desc: p.desc || '',
      sku: sku,
      image: image,
      price: price,
      compareAt: compareAt,
      available: available,
      fields: {
        title: tokenize(p.title),
        vendor: tokenize(p.vendor),
        type: tokenize(p.product_type),
        tags: tokenize((p.tags || []).join(' '))
      }
    };
  }

  function indexRecords(records) {
    Index.records = records;
    var vocab = new Map();
    for (var r = 0; r < records.length; r++) {
      var f = records[r].fields;
      for (var key in f) {
        var toks = f[key];
        for (var t = 0; t < toks.length; t++) {
          var tok = toks[t];
          if (tok.length < 2) continue;
          vocab.set(tok, (vocab.get(tok) || 0) + 1);
        }
      }
    }
    Index.vocab = vocab;
    Index.vocabList = Array.from(vocab.keys()).sort();
    var byHandle = {};
    for (var h = 0; h < records.length; h++) byHandle[records[h].handle] = records[h];
    Index.byHandle = byHandle;
    Index.ready = true;
  }

  var CACHE_KEY = 'pb_ss_catalog_v' + (CFG.cacheVersion || '1');

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      var ttl = (CFG.cacheTtlHours || 6) * 3600 * 1000;
      if (!obj || !obj.ts || !obj.products) return null;
      if (Date.now() - obj.ts > ttl) return null;
      return obj.products;
    } catch (e) { return null; }
  }

  function writeCache(slim) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), products: slim }));
    } catch (e) { /* quota — keep in memory for the session */ }
  }

  // Slim payload kept in cache (recomputed into records on load).
  function slimProduct(p) {
    return {
      id: p.id, handle: p.handle, title: p.title, vendor: p.vendor,
      product_type: p.product_type, tags: p.tags,
      desc: stripHtml(p.body_html).slice(0, 90),
      images: p.images && p.images[0] ? [{ src: p.images[0].src }] : [],
      featured_image: p.featured_image,
      variants: (p.variants || []).map(function (v) {
        return { price: v.price, compare_at_price: v.compare_at_price, available: v.available, sku: v.sku };
      })
    };
  }

  function fetchAllProducts() {
    var max = CFG.maxProducts || 5000, perPage = 250, collected = [];
    function fetchPage(page) {
      var sep = CFG.productsUrl.indexOf('?') > -1 ? '&' : '?';
      return fetch(CFG.productsUrl + sep + 'limit=' + perPage + '&page=' + page, { headers: { Accept: 'application/json' } })
        .then(function (res) {
          // 429 : Shopify plafonne /products.json des qu'on enchaine les pages.
          // On s'arrete proprement et on garde ce qui est deja collecte, au
          // lieu de rejeter la construction entiere et de perdre les 19 pages
          // precedentes.
          if (res.status === 429) return null;
          if (!res.ok) throw new Error('products.json ' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (data === null) return collected;
          var list = data && data.products ? data.products : [];
          for (var i = 0; i < list.length; i++) collected.push(slimProduct(list[i]));
          if (list.length === perPage && collected.length < max && page < 60) return fetchPage(page + 1);
          return collected;
        });
    }
    return fetchPage(1);
  }

  function ensureIndex() {
    if (Index.ready) return Promise.resolve(Index);
    if (Index.failed) return Promise.reject(new Error('index failed'));
    if (Index.loading) return Index.loading;
    Index.loading = new Promise(function (resolve, reject) {
      var cached = readCache();
      if (cached && cached.length) {
        try { indexRecords(cached.map(buildRecord)); resolve(Index); return; } catch (e) { /* refetch */ }
      }
      fetchAllProducts()
        .then(function (slim) {
          if (!slim.length) throw new Error('empty catalog');
          writeCache(slim);
          indexRecords(slim.map(buildRecord));
          resolve(Index);
        })
        .catch(function (err) {
          Index.failed = true;
          if (window.console) console.warn('[pb-smart-search] catalog unavailable:', err.message);
          reject(err);
        });
    });
    return Index.loading;
  }

  // ----------------------------------------------------------------------- //
  // Query correction (typo, prefix, de-concatenation)                       //
  // ----------------------------------------------------------------------- //

  // First vocab index whose token is >= term.
  function lowerBound(term) {
    var list = Index.vocabList, lo = 0, hi = list.length;
    while (lo < hi) { var mid = (lo + hi) >> 1; if (list[mid] < term) lo = mid + 1; else hi = mid; }
    return lo;
  }

  function isPrefixOfVocab(term) {
    if (term.length < 2) return false;
    var lo = lowerBound(term);
    return lo < Index.vocabList.length && Index.vocabList[lo].indexOf(term) === 0;
  }

  // Vocab words starting with prefix, ranked by frequency.
  function prefixVocab(prefix, limit) {
    if (prefix.length < 1) return [];
    var list = Index.vocabList, lo = lowerBound(prefix), out = [];
    for (var i = lo; i < list.length && out.length < 200; i++) {
      if (list[i].indexOf(prefix) !== 0) break;
      out.push(list[i]);
    }
    out.sort(function (a, b) { return Index.vocab.get(b) - Index.vocab.get(a); });
    return out.slice(0, limit);
  }

  // A split piece must be at least 3 chars (so a near-miss like "guitars" is
  // never chopped into "guita" + "rs") and be a real catalog word or a prefix.
  function isSplitPiece(piece) {
    if (piece.length < 3) return false;
    if (Index.vocab.has(piece)) return true;
    if (isPrefixOfVocab(piece)) return true;
    return false;
  }

  // "guitarhome" -> ["guitar","home"]; minimizes piece count.
  function wordBreak(term) {
    var n = term.length;
    if (n < 4) return null;
    var dp = new Array(n + 1); dp[0] = [];
    for (var i = 1; i <= n; i++) {
      dp[i] = null;
      for (var j = Math.max(0, i - 24); j < i; j++) {
        if (dp[j] === null) continue;
        var piece = term.slice(j, i);
        if (!isSplitPiece(piece)) continue;
        if (dp[i] === null || dp[j].length + 1 < dp[i].length) dp[i] = dp[j].concat([piece]);
      }
    }
    return dp[n] && dp[n].length >= 2 && dp[n].length <= 4 ? dp[n] : null;
  }

  // Split glued / CamelCase input on case boundaries, preserving the exact
  // letters: "GuitarsLPB" -> "Guitars LPB", "LPBGuitars" -> "LPB Guitars".
  // Already-spaced input ("Guitars LPB") is returned unchanged.
  function deglue(raw) {
    return String(raw || '')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')     // wordWord    -> word Word
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')  // ACRONYMWord -> ACRONYM Word
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Stays faithful to what the user typed. It only SPLITS glued words — first by
  // case (CamelCase/acronyms, keeping the original letters), then, for all-
  // lowercase glue, by the catalog dictionary. It never REPLACES a word, so
  // "Guitars LPB" stays "Guitars LPB" (no "LPB" -> "lp"). Typo tolerance is
  // handled at match time (fuzzy scoring) and by Shopify's own search.
  function correctQuery(raw) {
    var deglued = deglue(raw);
    var changed = normalize(deglued) !== normalize(raw);

    // Match terms: clean single-word tokens. tokenize() splits on punctuation too,
    // so "SL-50" becomes ["sl","50"] — never a broken multi-word term like "sl 50".
    var terms = [];
    var base = tokenize(deglued);
    for (var i = 0; i < base.length; i++) {
      var t = base[i];
      if (t.length < 2 || Index.vocab.has(t) || isPrefixOfVocab(t)) { terms.push(t); continue; }
      var wb = wordBreak(t); // dictionary split, for all-lowercase glue
      if (wb) { terms.push.apply(terms, wb); changed = true; continue; }
      terms.push(t); // keep as typed — never rewrite the word
    }
    var seen = {}, dterms = [];
    for (var j = 0; j < terms.length; j++) if (!seen[terms[j]]) { seen[terms[j]] = 1; dterms.push(terms[j]); }

    // Display string (suggestion + navigation): the de-glued query, with any
    // all-lowercase glued token split by the dictionary, original case preserved.
    var disp = deglued.split(/\s+/).map(function (dt) {
      var nt = normalize(dt);
      if (nt && nt.indexOf(' ') === -1 && !Index.vocab.has(nt) && !isPrefixOfVocab(nt)) {
        var w = wordBreak(nt);
        if (w) return w.join(' ');
      }
      return dt;
    });

    return { rawTerms: tokenize(raw), terms: dterms, changed: changed, correctedString: disp.join(' ') };
  }

  // ----------------------------------------------------------------------- //
  // Scoring                                                                 //
  // ----------------------------------------------------------------------- //

  function tokenScore(token, term) {
    if (token === term) return 1.0;
    if (token.indexOf(term) === 0) return 0.92;
    if (term.length >= 3 && token.indexOf(term) > 0) return 0.8;
    if (token.length >= 3 && term.indexOf(token) === 0 && term.length > token.length) return 0.78;
    var max = maxEditsFor(term.length);
    if (max === 0) return 0;
    var d = editDistance(term, token, max);
    if (d <= max) return 0.78 - (d - 1) * 0.14;
    return 0;
  }

  function bestTokenScore(tokens, term) {
    var best = 0;
    for (var t = 0; t < tokens.length; t++) {
      var s = tokenScore(tokens[t], term);
      if (s > best) best = s;
      if (best >= 1) break;
    }
    return best;
  }

  function bestFieldScore(record, term) {
    var f = record.fields, best = 0;
    for (var field in FIELD_WEIGHTS) {
      var s = bestTokenScore(f[field], term) * FIELD_WEIGHTS[field];
      if (s > best) best = s;
    }
    return best;
  }

  // All records matching ALL terms (word order irrelevant), best first.
  function runSearch(terms) {
    var out = [], records = Index.records;
    for (var r = 0; r < records.length; r++) {
      var rec = records[r], total = 0, ok = true;
      for (var t = 0; t < terms.length; t++) {
        var s = bestFieldScore(rec, terms[t]);
        if (s <= 0) { ok = false; break; }
        total += s;
      }
      if (!ok) continue;
      if (rec.available) total += 0.05;
      out.push({ rec: rec, score: total });
    }
    out.sort(function (a, b) { return b.score - a.score; });
    return out.map(function (x) { return x.rec; });
  }

  function smartSearch(raw) {
    var c = correctQuery(raw);
    var terms = c.terms.length ? c.terms : tokenize(raw);
    return { correction: c, terms: terms, results: runSearch(terms) };
  }

  // Re-rank merged results so products whose TITLE matches the query come first:
  // a product whose title contains ALL the query words wins (so searching a
  // product's name surfaces that product). Vendor/type/tag matches still count,
  // just lower — so brand/category searches keep working.
  function rankResults(records, terms) {
    if (!terms.length) return records;
    return records.map(function (rec) {
      var titleToks = (rec.fields && rec.fields.title) || tokenize(rec.title);
      var inTitle = 0, score = 0;
      for (var i = 0; i < terms.length; i++) {
        var ts = bestTokenScore(titleToks, terms[i]);
        if (ts > 0) inTitle++;
        score += (rec.fields ? bestFieldScore(rec, terms[i]) : ts) + ts; // title weighted
      }
      if (inTitle === terms.length) score += 100; // all query words appear in the title
      if (rec.available) score += 0.1;
      return { rec: rec, inTitle: inTitle, score: score };
    }).sort(function (a, b) {
      return b.inTitle - a.inTitle || b.score - a.score;
    }).map(function (x) { return x.rec; });
  }

  // ----------------------------------------------------------------------- //
  // Auxiliary result columns                                                //
  // ----------------------------------------------------------------------- //

  function buildSuggestions(correction, rawQuery) {
    var out = [], seen = {}, nq = normalize(rawQuery);
    function add(s) { s = s.trim(); if (s && s !== nq && !seen[s]) { seen[s] = 1; out.push(s); } }
    if (correction.changed && correction.correctedString) add(correction.correctedString);
    var terms = correction.rawTerms;
    var last = terms.length ? terms[terms.length - 1] : '';
    var head = terms.slice(0, -1).join(' ');
    if (last) prefixVocab(last, 6).forEach(function (w) { add((head ? head + ' ' : '') + w); });
    return out.slice(0, 5);
  }

  // Categories = product_type of the matched products, by frequency.
  function deriveCategories(records, limit) {
    var counts = {}, order = [];
    for (var i = 0; i < records.length; i++) {
      var ty = records[i].type;
      if (!ty) continue;
      if (!counts[ty]) { counts[ty] = 0; order.push(ty); }
      counts[ty]++;
    }
    order.sort(function (a, b) { return counts[b] - counts[a]; });
    return order.slice(0, limit).map(function (ty) { return { label: ty, count: counts[ty] }; });
  }

  // Pages passed from Liquid, fuzzy-matched against the query terms.
  var PAGES = [];
  function initPages() {
    PAGES = (CFG.pages || []).map(function (p) {
      return { title: p.title, url: p.url, tokens: tokenize(p.title) };
    });
  }
  function matchPages(terms, limit) {
    var out = [];
    for (var i = 0; i < PAGES.length; i++) {
      var pg = PAGES[i], total = 0, ok = true;
      for (var t = 0; t < terms.length; t++) {
        var s = bestTokenScore(pg.tokens, terms[t]);
        if (s <= 0) { ok = false; break; }
        total += s;
      }
      if (ok) out.push({ pg: pg, score: total });
    }
    out.sort(function (a, b) { return b.score - a.score; });
    return out.slice(0, limit).map(function (x) { return x.pg; });
  }

  // ----------------------------------------------------------------------- //
  // Formatting / markup helpers                                             //
  // ----------------------------------------------------------------------- //

  var money;
  try { money = new Intl.NumberFormat(CFG.locale || 'fr', { style: 'currency', currency: CFG.currency || 'EUR' }); }
  catch (e) { money = null; }

  function formatPrice(value) {
    if (value === null || value === undefined) return '';
    if (money) return money.format(value);
    return value.toFixed(2) + ' ' + (CFG.currency || '');
  }

  function sizedImage(src, w) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function highlight(title, terms) {
    var safe = esc(title);
    return safe.split(/(\s+)/).map(function (w) {
      var nw = normalize(w);
      if (!nw) return w;
      for (var i = 0; i < terms.length; i++) {
        if (nw === terms[i] || nw.indexOf(terms[i]) === 0) return '<mark class="pb-ss-mark">' + w + '</mark>';
      }
      return w;
    }).join('');
  }

  var ICON_SEARCH = '<svg class="pb-ss-ic" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="2"/><line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  var ICON_INFO = '<svg class="pb-ss-ic" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><line x1="12" y1="11" x2="12" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="7.5" r="1.2" fill="currentColor"/></svg>';

  function strings() { return CFG.strings || {}; }

  function searchURL(query, original) {
    var url = CFG.searchUrl + '?q=' + encodeURIComponent(query) + '&type=product';
    if (original && original !== query) url += '&pb_orig=' + encodeURIComponent(original);
    return url;
  }

  // One product card (shared by the dropdown and the results-page fallback).
  function productCardHTML(rec, terms, withHighlight) {
    var S = strings();
    var img = rec.image
      ? '<img class="pb-ss-card__img" src="' + esc(sizedImage(rec.image, 200)) + '" alt="" loading="lazy">'
      : '<span class="pb-ss-card__img pb-ss-card__img--ph"></span>';
    var title = withHighlight ? highlight(rec.title, terms) : esc(rec.title);
    var prices = '';
    if (CFG.showPrice && rec.price !== null) {
      var onSale = rec.compareAt !== null;
      prices = '<span class="pb-ss-card__prices">' +
        '<span class="pb-ss-card__price' + (onSale ? ' is-sale' : '') + '">' + esc(formatPrice(rec.price)) + '</span>' +
        (onSale ? '<s class="pb-ss-card__compare">' + esc(formatPrice(rec.compareAt)) + '</s>' : '') +
        '</span>';
    }
    return '<a class="pb-ss-card pb-ss-nav" href="' + esc(rec.url) + '">' +
      '<span class="pb-ss-card__imgwrap">' + img + '</span>' +
      '<span class="pb-ss-card__body">' +
        '<span class="pb-ss-card__title">' + title + '</span>' +
        (CFG.showDesc && rec.desc ? '<span class="pb-ss-card__desc">' + esc(rec.desc) + '</span>' : '') +
        (CFG.showSku && rec.sku ? '<span class="pb-ss-card__sku">' + esc(rec.sku) + '</span>' : '') +
        prices +
      '</span>' +
    '</a>';
  }

  // ----------------------------------------------------------------------- //
  // Shopify search parity                                                   //
  // The dropdown pulls products from Shopify's FULL search (the same engine  //
  // that powers the /search results page) via an alternate JSON view         //
  // (templates/search.suggest.liquid), NOT the predictive /search/suggest    //
  // .json — predictive matches more strictly and can return 0 where the page //
  // returns dozens. So the dropdown shows exactly what the page shows.       //
  // ----------------------------------------------------------------------- //

  var shopifyCache = {}; // query -> Promise<record[]>

  // Map a product from the alternate full-search view (prices in cents).
  function recordFromSearchItem(item) {
    var price = typeof item.price === 'number' ? item.price / 100 : null;
    var cmp = (typeof item.compare_at_price === 'number' && price !== null && item.compare_at_price > item.price)
      ? item.compare_at_price / 100 : null;
    return {
      handle: item.handle,
      url: item.url || (CFG.productUrlBase + item.handle),
      title: item.title || '',
      vendor: item.vendor || '',
      type: item.type || '',
      desc: item.desc || '',
      sku: item.sku || '',
      image: item.image || null,
      price: price,
      compareAt: cmp,
      available: item.available !== false,
      fields: null
    };
  }

  // Fetch products from the full-search alternate view. Prefer the rich local
  // index record when present (consistent data, no extra work); otherwise use
  // the data the view returned.
  function fetchShopifyProducts(query) {
    query = (query || '').trim();
    if (!query) return Promise.resolve([]);
    if (shopifyCache[query]) return shopifyCache[query];
    var url = CFG.searchUrl + '?q=' + encodeURIComponent(query) +
      '&type=product&view=' + (CFG.searchView || 'suggest');
    shopifyCache[query] = fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.text() : '[]'; })
      .then(function (text) { try { return JSON.parse(text); } catch (e) { return []; } })
      .then(function (list) {
        if (!Array.isArray(list)) return [];
        return list.map(function (item) {
          return (item && Index.byHandle[item.handle]) || recordFromSearchItem(item);
        });
      })
      .catch(function () { return []; });
    return shopifyCache[query];
  }

  // Shopify results first (page parity), then any extra our fuzzy index found.
  function mergeProducts(primary, extra, limit) {
    var out = [], seen = {};
    function add(r) { if (r && r.handle && !seen[r.handle]) { seen[r.handle] = 1; out.push(r); } }
    primary.forEach(add);
    extra.forEach(add);
    return out.slice(0, limit);
  }

  // Ask Shopify with the RAW query first — the words the user typed (e.g. "Guitars
  // LPB") are usually exact matches Shopify finds directly. Only if that returns
  // nothing do we try the corrected query (for real typos / glued words like
  // "GuitarsLPB"). Returns the products AND the query that actually worked, so
  // navigation (view-all / submit) points where the results really are.
  function shopifyForQuery(rawQuery, correctedQuery) {
    var raw = (rawQuery || '').trim();
    return fetchShopifyProducts(raw).then(function (rawRes) {
      if (rawRes.length || !correctedQuery || normalize(correctedQuery) === normalize(raw)) {
        return { products: rawRes, query: raw };
      }
      return fetchShopifyProducts(correctedQuery).then(function (corRes) {
        return { products: mergeProducts(rawRes, corRes, 20), query: corRes.length ? correctedQuery : raw };
      });
    });
  }

  // ----------------------------------------------------------------------- //
  // Dropdown UI                                                             //
  // ----------------------------------------------------------------------- //

  function debounce(fn, ms) {
    var t;
    return function () { var a = arguments, s = this; clearTimeout(t); t = setTimeout(function () { fn.apply(s, a); }, ms); };
  }

  function enhanceInput(input) {
    if (input.__pbSS) return;
    input.__pbSS = true;
    input.setAttribute('autocomplete', 'off');

    var isModal = input.hasAttribute('data-pb-ss-modal');
    var form = input.closest('form');
    var host = input.parentNode;
    if (!isModal && getComputedStyle(host).position === 'static') host.style.position = 'relative';

    var panel = document.createElement('div');
    panel.className = 'pb-ss-panel' + (isModal ? ' pb-ss-panel--modal' : '');
    panel.hidden = true;
    host.appendChild(panel);

    var state = { items: [], active: -1, lastQuery: '' };

    function close() { panel.hidden = true; state.active = -1; input.setAttribute('aria-expanded', 'false'); }
    function open() { if (panel.innerHTML) { panel.hidden = false; input.setAttribute('aria-expanded', 'true'); } }

    function paint(query, data, products, pending, navQuery) {
      var S = strings();
      var terms = data.terms;
      var suggestions = buildSuggestions(data.correction, query);
      var categories = deriveCategories(products, 5);
      var pages = matchPages(terms, 4);

      var aside = '';
      if (suggestions.length) {
        aside += '<section class="pb-ss-group"><h4 class="pb-ss-h">' + esc(S.popularSuggestions || 'Suggestions') + '</h4><ul class="pb-ss-lines">';
        suggestions.forEach(function (s) {
          aside += '<li><a class="pb-ss-line pb-ss-nav" href="' + esc(searchURL(s, query)) + '">' + ICON_SEARCH + '<span>' + esc(s) + '</span></a></li>';
        });
        aside += '</ul></section>';
      }
      if (categories.length) {
        aside += '<section class="pb-ss-group"><h4 class="pb-ss-h">' + esc(S.categories || 'Catégories') + '</h4><ul class="pb-ss-lines">';
        categories.forEach(function (c) {
          aside += '<li><a class="pb-ss-line pb-ss-cat" href="' + esc(searchURL(c.label)) + '"><span>' + esc(c.label) + '</span><em class="pb-ss-count">' + c.count + '</em></a></li>';
        });
        aside += '</ul></section>';
      }
      if (pages.length) {
        aside += '<section class="pb-ss-group"><h4 class="pb-ss-h">' + esc(S.pages || 'Pages') + '</h4><ul class="pb-ss-lines">';
        pages.forEach(function (p) {
          aside += '<li><a class="pb-ss-line pb-ss-nav" href="' + esc(p.url) + '">' + ICON_INFO + '<span>' + esc(p.title) + '</span></a></li>';
        });
        aside += '</ul></section>';
      }

      var main = '<h4 class="pb-ss-h">' + esc(S.products || 'Produits') + '</h4>';
      if (products.length) {
        main += '<div class="pb-ss-products">';
        products.forEach(function (rec) { main += productCardHTML(rec, terms, true); });
        main += '</div>';
      } else if (pending) {
        main += '<div class="pb-ss-empty">' + esc(S.searching || 'Recherche…') + '</div>';
      } else {
        main += '<div class="pb-ss-empty">' + esc(S.noResults || 'Aucun résultat pour') + ' « ' + esc(query) + ' »</div>';
      }

      var html = '<div class="pb-ss-cols">' +
        (aside ? '<aside class="pb-ss-aside">' + aside + '</aside>' : '') +
        '<div class="pb-ss-main">' + main + '</div>' +
      '</div>';

      if (products.length || pending) {
        html += '<a class="pb-ss-all pb-ss-nav" href="' + esc(searchURL(navQuery || query)) + '">' +
          esc(S.viewAll || 'Voir tous les résultats') +
          '<span class="pb-ss-all__arrow" aria-hidden="true">→</span></a>';
      }

      panel.innerHTML = html;
      state.items = Array.prototype.slice.call(panel.querySelectorAll('.pb-ss-nav'));
      state.active = -1;
      open();
    }

    // Instant paint from the client index, then mirror the /search results page
    // via Shopify search (raw query first, corrected as fallback) so the dropdown
    // matches the page and we remember which query actually returned results.
    function render(query) {
      var data = smartSearch(query);
      var limit = CFG.suggestions || 7;
      var terms = data.terms;
      var local = data.results;
      paint(query, data, rankResults(local, terms).slice(0, limit), true, query);
      if (CFG.useShopifyResults === false) { paint(query, data, rankResults(local, terms).slice(0, limit), false, query); return; }
      shopifyForQuery(query, data.correction.correctedString).then(function (res) {
        if (input.value.trim() !== query) return;
        state.effectiveQuery = res.query;
        state.effectiveFor = query;
        // Merge a larger pool (Shopify recall + local precision), then rank by
        // title so the product the user named surfaces first.
        var ranked = rankResults(mergeProducts(res.products, local, 40), terms);
        paint(query, data, ranked.slice(0, limit), false, res.query);
      }).catch(function () {
        if (input.value.trim() === query) paint(query, data, rankResults(local, terms).slice(0, limit), false, query);
      });
    }

    // NE PAS ATTENDRE L'INDEX POUR REPONDRE.
    //
    // Avant, chaque frappe attendait ensureIndex(), et son .catch() ne faisait
    // rien. Consequences mesurees sur cette boutique :
    //   · la premiere recherche attendait le telechargement de 20 pages de
    //     /products.json — d'ou la lenteur ;
    //   · /products.json repond 429 « too many requests » des qu'on enchaine
    //     les pages. Un seul echec posait Index.failed, et la liste deroulante
    //     restait vide POUR TOUJOURS chez ce visiteur — alors que la recherche
    //     Shopify, elle, aurait repondu.
    //
    // On peint donc immediatement : render() interroge /search?view=suggest,
    // c'est-a-dire l'index de Shopify, toujours a jour et sans plafond. Un
    // produit importe a l'instant y est. L'index local, lui, n'est plus qu'un
    // bonus — tolerance aux fautes de frappe et rappel supplementaire — et il
    // repeint quand il arrive, s'il arrive.
    var onType = debounce(function () {
      var q = input.value.trim();
      state.lastQuery = q;
      if (q.length < (CFG.minChars || 2)) { close(); return; }
      render(q);
      if (!Index.ready && !Index.failed) {
        ensureIndex().then(function () {
          if (input.value.trim() === q) render(q);
        }).catch(function () {});
      }
    }, CFG.debounce || 130);

    input.addEventListener('input', onType);
    input.addEventListener('focus', function () {
      ensureIndex().catch(function () {});
      if (state.lastQuery.length >= (CFG.minChars || 2) && panel.innerHTML) open();
    });

    function setActive(idx) {
      if (!state.items.length) return;
      if (state.active > -1) state.items[state.active].classList.remove('is-active');
      state.active = (idx + state.items.length) % state.items.length;
      var el = state.items[state.active];
      el.classList.add('is-active');
      el.scrollIntoView({ block: 'nearest' });
    }

    input.addEventListener('keydown', function (e) {
      if (panel.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(state.active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(state.active - 1); }
      else if (e.key === 'Enter') {
        if (state.active > -1) { e.preventDefault(); window.location.assign(state.items[state.active].href); }
      } else if (e.key === 'Escape') { close(); }
    });

    document.addEventListener('click', function (e) { if (!host.contains(e.target)) close(); });
    if (!isModal) input.addEventListener('blur', function () { setTimeout(close, 150); });

    // On submit, only redirect to the corrected query when the dropdown has
    // already confirmed that query returns Shopify results (state.effectiveQuery).
    // Otherwise submit the raw query untouched — never corrupt a working search.
    if (form) {
      form.addEventListener('submit', function (e) {
        var q = input.value.trim();
        if (!q) return;
        if (state.effectiveFor === q && state.effectiveQuery && normalize(state.effectiveQuery) !== normalize(q)) {
          e.preventDefault();
          window.location.assign(searchURL(state.effectiveQuery, q));
        }
      });
    }
  }

  // ----------------------------------------------------------------------- //
  // Search results page: "did you mean" note + zero-results fallback        //
  // ----------------------------------------------------------------------- //

  function initResultsPage() {
    var meta = document.getElementById('pb-ss-meta');
    if (!meta) return;
    var old = document.querySelectorAll('.pb-ss-note, .pb-ss-fallback');
    for (var o = 0; o < old.length; o++) old[o].remove();

    var S = strings();
    var count = parseInt(meta.getAttribute('data-count'), 10) || 0;
    var terms = meta.getAttribute('data-terms') || '';
    var original = new URLSearchParams(window.location.search).get('pb_orig');
    var header = document.querySelector('.main-search__header') || meta.parentNode;

    if (original && normalize(original) !== normalize(terms)) {
      var note = document.createElement('div');
      note.className = 'pb-ss-note';
      note.innerHTML = (S.resultsFor || 'Résultats pour') + ' <strong>« ' + esc(terms) + ' »</strong>. ' +
        (S.searchInstead || 'Rechercher plutôt') + ' <a href="' + esc(CFG.searchUrl + '?q=' + encodeURIComponent(original) + '&type=product') + '">« ' + esc(original) + ' »</a> ?';
      header.insertBefore(note, header.firstChild);
    }

    if (count === 0 && terms) {
      ensureIndex().then(function () {
        if (document.querySelector('.pb-ss-fallback')) return;
        var data = smartSearch(original || terms);
        if (!data.results.length) return;
        var block = document.createElement('div');
        block.className = 'pb-ss-fallback';
        var html = '<p class="pb-ss-fallback__heading">' + esc(S.fallbackHeading || 'Aucun résultat exact. Voici des produits proches :') + '</p><div class="pb-ss-fallback__grid">';
        data.results.slice(0, 12).forEach(function (rec) { html += productCardHTML(rec, data.terms, false); });
        html += '</div>';
        block.innerHTML = html;
        header.parentNode.insertBefore(block, header.nextSibling);
      }).catch(function () {});
    }
  }

  // ----------------------------------------------------------------------- //
  // Boot                                                                    //
  // ----------------------------------------------------------------------- //

  function boot() {
    initPages();
    var selectors = CFG.selectors && CFG.selectors.length ? CFG.selectors : ['.pb-header__search-input'];
    var seen = [];
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (input) {
        if (seen.indexOf(input) > -1) return;
        seen.push(input);
        enhanceInput(input);
      });
    });
    initResultsPage();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);

  window.PBSmartSearch = {
    ensureIndex: ensureIndex, search: smartSearch, correct: correctQuery, index: Index,
    shopifyProducts: fetchShopifyProducts, merge: mergeProducts, rank: rankResults
  };
})();
